import { Chip, chipClasses, styled } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import type { ArtifactFilter } from "../store/agent-chat-slice"
import { CONVERSATION_COLUMN_MAX_WIDTH } from "./composer-chrome"
import { SEARCH_WIDTH, ToolbarWithSearch } from "./toolbar-with-search"

declare module "@mui/x-data-grid-premium" {
  interface ToolbarPropsOverrides {
    placeholder?: string
    ariaLabel?: string
  }
}

const SURFACE_COLUMN_GAP = 1

// The same centred column the transcript and composer use, so the list lines up with the rest of the surface
// instead of running full-bleed. width:100% because auto cross-axis margins turn off the flex stretch.
export const surfaceRootSx = (containerName: string) => ({
  display: "flex", flexDirection: "column", flex: 1, minHeight: 0, p: 1.5, gap: SURFACE_COLUMN_GAP,
  width: "100%", maxWidth: CONVERSATION_COLUMN_MAX_WIDTH, mx: "auto",
  containerType: "inline-size", containerName,
}) satisfies SxProps<Theme>

// mb on top of the column gap: the pills read as their own control strip, not as part of the title.
export const SURFACE_HEADER_SX = { display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap", mb: 1 } satisfies SxProps<Theme>

// Centred, because the lifted search shares this row's height (GRID_SX).
export const SURFACE_PILLS_SX = {
  display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", minHeight: "2.5rem",
} satisfies SxProps<Theme>

// MUI palette values as CSS vars, not theme.palette.* — cssVariables freezes those to the light scheme.
export const HOVER_SURFACE = "var(--mui-palette-action-selected)"

/**
 * One style injection for the whole filter row rather than an `sx` per Chip. Selected takes the app's
 * primary straight off the palette (#03a9f4 light, #0277bd dark, where a low-alpha wash all but
 * disappeared) and hovers to the palette's own primary-hover rather than to a grey.
 *
 * Hover is keyed on Chip's own `&.MuiChip-clickable:hover`, which is where MUI declares it — two classes
 * and a pseudo-class, so a plain `&:hover` cannot reach it from `styled` OR from `sx`. Measured both ways.
 */
export const FilterPill = styled(Chip, {
  shouldForwardProp: prop => prop !== "isSelected",
})<{ isSelected: boolean }>(({ isSelected }) => ({
  // The label owns a Chip's horizontal padding — setting it on the root only moves the border.
  height: 28,
  borderRadius: 14,
  [`& .${chipClasses.label}`]: { paddingLeft: 14, paddingRight: 14 },
  ...(isSelected
    ? {
      fontWeight: 500,
      borderColor: "transparent",
      backgroundColor: "var(--app-primary)",
      color: "var(--mui-palette-primary-contrastText)",
      [`&.${chipClasses.clickable}:hover`]: { backgroundColor: "var(--app-opcolor-material-hover)" },
    }
    : {
      [`&.${chipClasses.clickable}:hover`]: { backgroundColor: HOVER_SURFACE },
    }),
}))

export const surfaceGridBaseSx = (theme: Theme, containerName: string) => ({
  flex: 1,
  minHeight: 0,
  border: 0,
  position: "relative",
  // Lifts the toolbar's search onto the pills' row; the overflow pair is what stops the grid clipping it there.
  [`@container ${containerName} (min-width: calc(${SEARCH_WIDTH} * 2))`]: {
    overflow: "visible",
    "& .MuiDataGrid-mainContent": { overflow: "visible" },
    "& .MuiDataGrid-toolbar": {
      position: "absolute",
      bottom: `calc(100% + ${theme.spacing(SURFACE_COLUMN_GAP)})`,
      right: 0,
      left: "auto",
      width: "auto",
      minHeight: 0,
      padding: 0,
      border: 0,
      backgroundColor: "transparent",
    },
  },
}) satisfies SxProps<Theme>

export const SURFACE_CONTAINER = "kelly-artifacts-surface"
// No column menus, no resize handles, no header separators: the grid is here for a sortable, searchable
// list of four fields, and every one of those affordances offers something this list cannot do.
export const GRID_SX = ((theme: Theme) => ({
  ...surfaceGridBaseSx(theme, SURFACE_CONTAINER),
  "& .MuiDataGrid-columnSeparator": { display: "none" },
  // The whole row opens the artifact, so the whole row is the affordance: it takes the pointer and it is
  // what highlights. The one exception is the Download button, which stops the click.
  "& .MuiDataGrid-row": { cursor: "pointer" },
  "& .MuiDataGrid-row:hover": { backgroundColor: HOVER_SURFACE },
  // A mouse click otherwise leaves a focus ring around the single cell it landed on, which reads as "this
  // cell is selected" on a surface where cells mean nothing. :focus-visible still fires for keyboard
  // navigation, so the grid keeps its focus indicator where it is the only cue.
  "& .MuiDataGrid-cell:focus": { outline: "none" },
  // Centring for the two cells the grid does not lay out itself, as one rule on the parent rather than an
  // sx on every rendered cell. Scoped by field on purpose: the grid gives `.cell` text-overflow: ellipsis,
  // which a flex container would stop applying, and the Name cell depends on it.
  "& .MuiDataGrid-cell[data-field='sharedAt'], & .MuiDataGrid-cell[data-field='actions']": {
    display: "flex",
    alignItems: "center",
  },
})) satisfies SxProps<Theme>
export const CENTER_SX = { display: "flex", justifyContent: "center", p: 3 } satisfies SxProps<Theme>
export const EMPTY_SX = { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 0.5, px: 3, py: 6 } satisfies SxProps<Theme>
export const EMPTY_GLYPH_SX = { fontSize: "2.5rem", mb: 1, color: "text.disabled" } satisfies SxProps<Theme>
export const EMPTY_BODY_SX = { color: "text.secondary", maxWidth: "22rem" } satisfies SxProps<Theme>

// Four different facts, so four headings — and each body says what would put something here.
export const EMPTY_COPY: Record<ArtifactFilter, { heading: string, body: string }> = {
  all: { heading: "No artifacts yet", body: "Share a file from one of your conversations and it will show up here." },
  yours: { heading: "You haven't shared anything yet", body: "Open a file Kelly made and use Share to give a colleague access." },
  shared: { heading: "Nothing shared with you yet", body: "When a colleague shares an artifact with you or one of your teams, it shows up here." },
  scheduled: { heading: "No reports yet", body: "Reports land here once a workflow runs." },
}

export function ArtifactsToolbar({ placeholder = "Search artifacts…", ariaLabel = "Search artifacts" }: { placeholder?: string, ariaLabel?: string }) {
  return <ToolbarWithSearch placeholder={placeholder} ariaLabel={ariaLabel} />
}
